import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface Lot { id: string; lot_number: string; address: string; voting_interest: number; is_association_owned: number }
interface Resident { id: string; name: string; email: string }
interface LotResident { resident_id: string; role_on_lot: string; interest_pct: number; name: string; email: string }

export function Lots() {
  const qc = useQueryClient();
  const lotsQ = useQuery<{ lots: Lot[] }>({ queryKey: ['lots'], queryFn: () => api.get('/lots') });
  const residentsQ = useQuery<{ residents: Resident[] }>({ queryKey: ['residents'], queryFn: () => api.get('/residents') });

  const [creating, setCreating] = useState(false);
  const [selectedLot, setSelectedLot] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: (b: { lotNumber: string; address: string; votingInterest: number; isAssociationOwned: boolean }) =>
      api.post('/lots', b),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['lots'] }); setCreating(false); },
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Lots</h1>
        <button onClick={() => setCreating(true)} className="px-3 py-1.5 bg-brand text-white rounded text-sm hover:bg-brand-600">Add lot</button>
      </div>

      <div className="bg-white border border-slate-200 rounded">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b">
              <th className="px-3 py-2">Lot #</th>
              <th className="px-3 py-2">Address</th>
              <th className="px-3 py-2">Voting interest</th>
              <th className="px-3 py-2">Association-owned</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {lotsQ.data?.lots.map((l) => (
              <tr key={l.id} className="border-b last:border-b-0">
                <td className="px-3 py-2 font-medium">{l.lot_number}</td>
                <td className="px-3 py-2 text-slate-600">{l.address}</td>
                <td className="px-3 py-2">{l.voting_interest.toFixed(2)}</td>
                <td className="px-3 py-2">{l.is_association_owned ? 'Yes' : 'No'}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setSelectedLot(l.id)} className="text-brand text-xs hover:underline">Manage owners</button>
                </td>
              </tr>
            ))}
            {lotsQ.data && lotsQ.data.lots.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">No lots yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {creating && (
        <Modal onClose={() => setCreating(false)} title="Add lot">
          <CreateLotForm onSubmit={(v) => createMut.mutate(v)} loading={createMut.isPending} error={createMut.error?.message ?? null} />
        </Modal>
      )}

      {selectedLot && (
        <LotResidentManager lotId={selectedLot} residents={residentsQ.data?.residents ?? []} onClose={() => setSelectedLot(null)} />
      )}
    </div>
  );
}

function CreateLotForm({ onSubmit, loading, error }: { onSubmit: (v: { lotNumber: string; address: string; votingInterest: number; isAssociationOwned: boolean }) => void; loading: boolean; error: string | null }) {
  const [v, setV] = useState({ lotNumber: '', address: '', votingInterest: 1.0, isAssociationOwned: false });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(v); }} className="space-y-3">
      <label className="block"><span className="text-sm font-medium">Lot number</span><input required value={v.lotNumber} onChange={(e) => setV({ ...v, lotNumber: e.target.value })} className="mt-1 w-full border border-slate-300 rounded px-3 py-2" /></label>
      <label className="block"><span className="text-sm font-medium">Address</span><input required value={v.address} onChange={(e) => setV({ ...v, address: e.target.value })} className="mt-1 w-full border border-slate-300 rounded px-3 py-2" /></label>
      <label className="block"><span className="text-sm font-medium">Voting interest <span className="text-slate-400 font-normal">(default 1.0 — fractional only if your declaration says so)</span></span><input type="number" step="0.01" required value={v.votingInterest} onChange={(e) => setV({ ...v, votingInterest: Number(e.target.value) })} className="mt-1 w-full border border-slate-300 rounded px-3 py-2" /></label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v.isAssociationOwned} onChange={(e) => setV({ ...v, isAssociationOwned: e.target.checked })} />Association-owned (LA RS 9:1141.28(C) proportional voting)</label>
      {error && <div className="text-sm text-red-600">{error}</div>}
      <button type="submit" disabled={loading} className="w-full bg-brand text-white py-2 rounded font-medium disabled:opacity-50">{loading ? 'Saving...' : 'Create lot'}</button>
    </form>
  );
}

function LotResidentManager({ lotId, residents, onClose }: { lotId: string; residents: Resident[]; onClose: () => void }) {
  const qc = useQueryClient();
  const lr = useQuery<{ residents: LotResident[] }>({ queryKey: ['lot-residents', lotId], queryFn: () => api.get(`/lots/${lotId}/residents`) });
  const linkMut = useMutation({
    mutationFn: (b: { residentId: string; roleOnLot: string; interestPct: number; lotId: string }) => api.post(`/lots/${lotId}/residents`, b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lot-residents', lotId] }),
  });
  const [residentId, setResidentId] = useState(residents[0]?.id ?? '');
  const [role, setRole] = useState('primary_owner');
  const [pct, setPct] = useState(100);
  const totalAssigned = (lr.data?.residents ?? []).reduce((s, r) => s + r.interest_pct, 0);

  return (
    <Modal onClose={onClose} title="Manage owners on lot">
      <p className="text-xs text-slate-500 mb-3">Sum of interest percentages must equal 100%. Currently assigned: <strong>{totalAssigned.toFixed(2)}%</strong>.</p>
      <div className="border border-slate-200 rounded mb-4">
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b"><th className="px-2 py-1">Owner</th><th className="px-2 py-1">Role</th><th className="px-2 py-1">Interest %</th></tr></thead>
          <tbody>
            {lr.data?.residents.map((r) => (
              <tr key={r.resident_id} className="border-b last:border-b-0"><td className="px-2 py-1">{r.name}</td><td className="px-2 py-1">{r.role_on_lot}</td><td className="px-2 py-1">{r.interest_pct.toFixed(2)}%</td></tr>
            ))}
            {lr.data && lr.data.residents.length === 0 && <tr><td colSpan={3} className="px-2 py-3 text-center text-slate-500">No owners linked yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {totalAssigned < 100 && residents.length > 0 && (
        <div className="space-y-2">
          <select value={residentId} onChange={(e) => setResidentId(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
            {residents.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.email})</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <select value={role} onChange={(e) => setRole(e.target.value)} className="border border-slate-300 rounded px-2 py-1 text-sm">
              <option value="primary_owner">Primary owner</option>
              <option value="co_owner">Co-owner</option>
              <option value="usufructuary">Usufructuary</option>
              <option value="lessee">Lessee</option>
            </select>
            <input type="number" step="0.01" max={100 - totalAssigned} value={pct} onChange={(e) => setPct(Number(e.target.value))} className="border border-slate-300 rounded px-2 py-1 text-sm" />
          </div>
          {linkMut.error && <div className="text-red-600 text-sm">{linkMut.error.message}</div>}
          <button onClick={() => linkMut.mutate({ lotId, residentId, roleOnLot: role, interestPct: pct })} disabled={linkMut.isPending}
            className="w-full bg-brand text-white py-1.5 rounded text-sm disabled:opacity-50">{linkMut.isPending ? 'Linking...' : 'Link owner'}</button>
        </div>
      )}
    </Modal>
  );
}

function Modal({ children, onClose, title }: { children: React.ReactNode; onClose: () => void; title: string }) {
  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
